import { Feather } from "@expo/vector-icons";
import { useMemo, useState } from "react";
import {
  Image,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Share,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { makeVideoShareUrl, type ShareSource } from "../../services/shareAttribution";
import { beginShare, recordShareOpen } from "../../services/shareTracking";

export type ShareableVideo = {
  id: string;
  title: string;
  thumbnail?: string;
};

type VideoShareButtonProps = {
  video: ShareableVideo;
  variant?: "toolbar" | "feed";
};

type BrowserShareNavigator = Navigator & {
  share?: (data: { title: string; text: string; url: string }) => Promise<void>;
};

type ShareTarget = {
  key: string;
  label: string;
  hint: string;
  icon: string;
  color: string;
  url?: string;
};

const DESKTOP_APP_TARGETS: ShareTarget[] = [
  {
    key: "wechat",
    label: "微信电脑版",
    hint: "复制后粘贴",
    icon: "message-circle",
    color: "#07c160",
  },
  {
    key: "douyin",
    label: "抖音电脑版",
    hint: "复制后发布",
    icon: "music",
    color: "#161823",
  },
  {
    key: "discord",
    label: "Discord",
    hint: "复制后粘贴",
    icon: "message-square",
    color: "#5865f2",
  },
  {
    key: "dingtalk",
    label: "钉钉",
    hint: "复制后粘贴",
    icon: "send",
    color: "#1677ff",
  },
  {
    key: "wecom",
    label: "企业微信",
    hint: "复制后粘贴",
    icon: "briefcase",
    color: "#2d8cf0",
  },
  {
    key: "feishu",
    label: "飞书",
    hint: "复制后粘贴",
    icon: "navigation",
    color: "#3370ff",
  },
];

export function videoShareUrl(videoId: string) {
  return makeVideoShareUrl(videoId, typeof window === "undefined" ? undefined : window.location?.href);
}

async function copyText(value: string) {
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  if (typeof document === "undefined") throw new Error("clipboard unavailable");
  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  textarea.remove();
  if (!copied) throw new Error("clipboard unavailable");
}

export default function VideoShareButton({
  video,
  variant = "toolbar",
}: VideoShareButtonProps) {
  const [visible, setVisible] = useState(false);
  const [notice, setNotice] = useState("");
  const shareUrl = useMemo(() => videoShareUrl(video.id), [video.id]);
  const directTargetsForUrl = (shareUrl: string): ShareTarget[] => {
    const shareMessage = `${video.title}\n${shareUrl}`;
    const url = encodeURIComponent(shareUrl);
    const title = encodeURIComponent(video.title);
    const message = encodeURIComponent(shareMessage);
    const thumbnail = encodeURIComponent(
      video.thumbnail && typeof window !== "undefined"
        ? new URL(video.thumbnail, window.location.origin).toString()
        : video.thumbnail || "",
    );
    return [
      {
        key: "weibo",
        label: "微博",
        hint: "发布分享",
        icon: "radio",
        color: "#e6162d",
        url: `https://service.weibo.com/share/share.php?url=${url}&title=${title}&pic=${thumbnail}`,
      },
      {
        key: "qq",
        label: "QQ 好友",
        hint: "选择好友",
        icon: "message-circle",
        color: "#12b7f5",
        url: `https://connect.qq.com/widget/shareqq/index.html?url=${url}&title=${title}&summary=${title}&pics=${thumbnail}`,
      },
      {
        key: "qzone",
        label: "QQ 空间",
        hint: "发布动态",
        icon: "star",
        color: "#f5b500",
        url: `https://sns.qzone.qq.com/cgi-bin/qzshare/cgi_qzshare_onekey?url=${url}&title=${title}&summary=${title}&pics=${thumbnail}`,
      },
      {
        key: "facebook",
        label: "Facebook",
        hint: "Share",
        icon: "facebook",
        color: "#1877f2",
        url: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      },
      {
        key: "x",
        label: "X",
        hint: "Post",
        icon: "twitter",
        color: "#111111",
        url: `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
      },
      {
        key: "whatsapp",
        label: "WhatsApp",
        hint: "发送消息",
        icon: "phone",
        color: "#25d366",
        url: `https://api.whatsapp.com/send?text=${message}`,
      },
      {
        key: "telegram",
        label: "Telegram",
        hint: "选择聊天",
        icon: "send",
        color: "#229ed9",
        url: `https://t.me/share/url?url=${url}&text=${title}`,
      },
      {
        key: "line",
        label: "LINE",
        hint: "发送给好友",
        icon: "message-square",
        color: "#06c755",
        url: `https://social-plugins.line.me/lineit/share?url=${url}`,
      },
      {
        key: "linkedin",
        label: "LinkedIn",
        hint: "创建帖子",
        icon: "linkedin",
        color: "#0a66c2",
        url: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      },
      {
        key: "reddit",
        label: "Reddit",
        hint: "Submit",
        icon: "message-circle",
        color: "#ff4500",
        url: `https://www.reddit.com/submit?url=${url}&title=${title}`,
      },
      {
        key: "pinterest",
        label: "Pinterest",
        hint: "保存 Pin",
        icon: "bookmark",
        color: "#bd081c",
        url: `https://www.pinterest.com/pin/create/button/?url=${url}&media=${thumbnail}&description=${title}`,
      },
      {
        key: "tumblr",
        label: "Tumblr",
        hint: "Create post",
        icon: "edit-3",
        color: "#35465c",
        url: `https://www.tumblr.com/widgets/share/tool?canonicalUrl=${url}&title=${title}&caption=${title}`,
      },
      {
        key: "vk",
        label: "VK",
        hint: "Share",
        icon: "users",
        color: "#0077ff",
        url: `https://vk.com/share.php?url=${url}&title=${title}&image=${thumbnail}`,
      },
      {
        key: "bluesky",
        label: "Bluesky",
        hint: "Compose",
        icon: "cloud",
        color: "#1185fe",
        url: `https://bsky.app/intent/compose?text=${message}`,
      },
    ];
  };
  const directTargets = directTargetsForUrl(shareUrl);

  const updateNotice = (message: string) => {
    setNotice(message);
    if (typeof window !== "undefined") {
      window.setTimeout(() => setNotice(""), 4200);
    }
  };

  const copy = async (source: ShareSource, successMessage: string, linkOnly = false) => {
    const attempt = beginShare(video, source, "clipboard");
    try {
      await copyText(linkOnly ? attempt.url : attempt.message);
      attempt.finish("copied");
      updateNotice(successMessage);
    } catch {
      attempt.finish("failed");
      updateNotice("复制失败，请选中地址栏中的链接手动复制");
    }
  };

  const openSystemShare = async () => {
    const attempt = beginShare(video, "system", "system");
    if (Platform.OS !== "web") {
      try {
        const result = await Share.share({ title: video.title, message: attempt.message, url: attempt.url });
        attempt.finish(result.action === Share.dismissedAction ? "cancelled" : "system_handoff");
      } catch { attempt.finish("failed"); }
      return;
    }

    const browserNavigator = navigator as BrowserShareNavigator;
    if (browserNavigator.share) {
      try {
        await browserNavigator.share({
          title: video.title,
          text: video.title,
          url: attempt.url,
        });
        attempt.finish("system_handoff");
        updateNotice("已交给系统分享，请在应用中完成发送");
        return;
      } catch (error) {
        if ((error as { name?: string })?.name === "AbortError") {
          attempt.finish("cancelled");
          updateNotice("已取消分享");
          return;
        }
      }
    }

    try {
      await copyText(attempt.message);
      attempt.finish("copied", "clipboard_fallback");
      updateNotice("系统分享不可用，标题和链接已复制");
    } catch {
      attempt.finish("failed", "clipboard_fallback");
      updateNotice("系统分享和复制均不可用，请手动复制链接");
    }
  };

  const openDirectTarget = (target: ShareTarget) => {
    if (!target.url || typeof window === "undefined") return;
    const attempt = beginShare(video, target.key as ShareSource, "web");
    try {
      const tracked = directTargetsForUrl(attempt.url).find((item) => item.key === target.key);
      if (!tracked?.url) throw new Error("Unknown share target");
      window.open(tracked.url, "_blank", "noopener,noreferrer,width=760,height=680");
      // noopener can return null even on success: never claim a sent post or
      // infer popup blocking from the returned window handle.
      attempt.finish("window_requested");
      updateNotice(`正在打开 ${target.label}；若未出现，请允许浏览器弹窗`);
    } catch { attempt.finish("failed"); updateNotice("无法打开分享页，请重试或复制链接"); }
  };

  const openButton = () => {
    recordShareOpen(video);
    if (Platform.OS !== "web") {
      void openSystemShare();
      return;
    }
    setNotice("");
    setVisible(true);
  };

  return (
    <>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={`分享视频：${video.title}`}
        onPress={openButton}
        style={({ pressed }) => [
          variant === "feed" ? styles.feedButton : styles.toolbarButton,
          pressed && styles.buttonPressed,
        ]}
      >
        <Feather
          name="share-2"
          size={variant === "feed" ? 32 : 15}
          color={variant === "feed" ? "#ffffff" : "#bdeee0"}
        />
        <Text
          style={
            variant === "feed"
              ? styles.feedButtonText
              : styles.toolbarButtonText
          }
        >
          分享
        </Text>
      </Pressable>

      <Modal
        transparent
        visible={visible}
        animationType="fade"
        onRequestClose={() => setVisible(false)}
      >
        <View style={styles.backdrop}>
          <Pressable
            style={StyleSheet.absoluteFillObject}
            onPress={() => setVisible(false)}
          />
          <View style={styles.sheet}>
            <View style={styles.header}>
              <View style={styles.headerTitleRow}>
                <View style={styles.headerIcon}>
                  <Feather name="share-2" size={20} color="#05110d" />
                </View>
                <View style={styles.headerCopy}>
                  <Text style={styles.title}>分享这个视频</Text>
                  <Text style={styles.subtitle}>电脑版可用分享方式</Text>
                </View>
              </View>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="关闭分享面板"
                style={styles.closeButton}
                onPress={() => setVisible(false)}
              >
                <Feather name="x" size={22} color="#e8f1ee" />
              </Pressable>
            </View>

            <View style={styles.videoSummary}>
              {video.thumbnail ? (
                <Image
                  source={{ uri: video.thumbnail }}
                  style={styles.thumbnail}
                  resizeMode="cover"
                />
              ) : (
                <View style={[styles.thumbnail, styles.thumbnailFallback]}>
                  <Feather name="play" size={24} color="#28c99a" />
                </View>
              )}
              <View style={styles.videoCopy}>
                <Text numberOfLines={2} style={styles.videoTitle}>
                  {video.title}
                </Text>
                <Text numberOfLines={1} style={styles.videoUrl}>
                  {shareUrl}
                </Text>
              </View>
            </View>

            {notice ? (
              <View style={styles.notice}>
                <Feather name="check-circle" size={15} color="#77e6c3" />
                <Text style={styles.noticeText}>{notice}</Text>
              </View>
            ) : null}

            <ScrollView
              style={styles.scroll}
              contentContainerStyle={styles.scrollContent}
              showsVerticalScrollIndicator
            >
              <View style={styles.quickActions}>
                <Pressable
                  style={styles.primaryAction}
                  onPress={() => void openSystemShare()}
                >
                  <Feather name="monitor" size={18} color="#05110d" />
                  <Text style={styles.primaryActionText}>
                    Windows 系统分享
                  </Text>
                </Pressable>
                <Pressable
                  style={styles.secondaryAction}
                  onPress={() => void copy("copy_link", "视频链接已复制", true)}
                >
                  <Feather name="link" size={17} color="#bdeee0" />
                  <Text style={styles.secondaryActionText}>复制链接</Text>
                </Pressable>
                <Pressable
                  style={styles.secondaryAction}
                  onPress={() => void copy("copy_text", "标题和链接已复制")}
                >
                  <Feather name="copy" size={17} color="#bdeee0" />
                  <Text style={styles.secondaryActionText}>复制文案</Text>
                </Pressable>
              </View>

              <Text style={styles.groupTitle}>电脑客户端</Text>
              <Text style={styles.groupDescription}>
                浏览器无法指定这些应用的聊天或发布窗口；点击后复制文案，再到电脑客户端粘贴。
              </Text>
              <View style={styles.targetGrid}>
                {DESKTOP_APP_TARGETS.map((target) => (
                  <ShareTargetButton
                    key={target.key}
                    target={target}
                    onPress={() =>
                      void copy(
                        target.key as ShareSource,
                        `标题和链接已复制，请打开“${target.label}”粘贴分享`,
                      )
                    }
                  />
                ))}
              </View>

              <Text style={styles.groupTitle}>网页可直接分享</Text>
              <Text style={styles.groupDescription}>
                点击后打开对应平台的官方分享页面。
              </Text>
              <View style={styles.targetGrid}>
                {directTargets.map((target) => (
                  <ShareTargetButton
                    key={target.key}
                    target={target}
                    onPress={() => openDirectTarget(target)}
                  />
                ))}
              </View>

              <Text style={styles.groupTitle}>其他方式</Text>
              <View style={styles.targetGrid}>
                <ShareTargetButton
                  target={{
                    key: "email",
                    label: "电子邮件",
                    hint: "发送邮件",
                    icon: "mail",
                    color: "#5c6bc0",
                  }}
                  onPress={() => {
                    if (typeof window !== "undefined") {
                      const attempt = beginShare(video, "email", "email");
                      try {
                        window.location.href = `mailto:?subject=${encodeURIComponent(video.title)}&body=${encodeURIComponent(attempt.message)}`;
                        attempt.finish("email_requested");
                      } catch { attempt.finish("failed"); }
                    }
                  }}
                />
                <ShareTargetButton
                  target={{
                    key: "print",
                    label: "打印 / PDF",
                    hint: "保存页面",
                    icon: "printer",
                    color: "#6b7280",
                  }}
                  onPress={() => {
                    if (typeof window === "undefined") return;
                    const attempt = beginShare(video, "print", "print");
                    try { window.print(); attempt.finish("print_requested"); }
                    catch { attempt.finish("failed"); }
                  }}
                />
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>
    </>
  );
}

function ShareTargetButton({
  target,
  onPress,
}: {
  target: ShareTarget;
  onPress: () => void;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`分享到${target.label}`}
      style={({ pressed }) => [
        styles.targetButton,
        pressed && styles.targetButtonPressed,
      ]}
      onPress={onPress}
    >
      <View style={[styles.targetIcon, { backgroundColor: target.color }]}>
        <Feather name={target.icon as never} size={19} color="#ffffff" />
      </View>
      <View style={styles.targetCopy}>
        <Text numberOfLines={1} style={styles.targetLabel}>
          {target.label}
        </Text>
        <Text numberOfLines={1} style={styles.targetHint}>
          {target.hint}
        </Text>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  toolbarButton: {
    height: 34,
    paddingHorizontal: 11,
    borderRadius: 7,
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    flexShrink: 0,
    backgroundColor: "#0a211a",
    borderWidth: 1,
    borderColor: "#2f765f",
    cursor: "pointer",
  } as never,
  toolbarButtonText: {
    color: "#bdeee0",
    fontSize: 13,
    fontWeight: "700",
  },
  feedButton: {
    paddingBottom: 12,
    alignItems: "center",
  },
  feedButtonText: {
    minWidth: 42,
    marginTop: 4,
    color: "#ffffff",
    fontSize: 12,
    textAlign: "center",
  },
  buttonPressed: { opacity: 0.7, transform: [{ scale: 0.98 }] },
  backdrop: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    backgroundColor: "rgba(0,0,0,.78)",
  },
  sheet: {
    width: "94%",
    maxWidth: 880,
    maxHeight: "90%",
    overflow: "hidden",
    borderRadius: 19,
    borderWidth: 1,
    borderColor: "#2d6655",
    backgroundColor: "#07130f",
    shadowColor: "#000",
    shadowOpacity: 0.65,
    shadowRadius: 36,
    shadowOffset: { width: 0, height: 16 },
  },
  header: {
    minHeight: 74,
    paddingHorizontal: 20,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottomWidth: 1,
    borderBottomColor: "#1b4035",
  },
  headerTitleRow: {
    flex: 1,
    minWidth: 0,
    flexDirection: "row",
    alignItems: "center",
  },
  headerIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
    backgroundColor: "#28c99a",
  },
  headerCopy: { flex: 1, minWidth: 0 },
  title: { color: "#f2f5f4", fontSize: 19, fontWeight: "800" },
  subtitle: { marginTop: 3, color: "#82918c", fontSize: 12 },
  closeButton: {
    width: 38,
    height: 38,
    marginLeft: 12,
    borderRadius: 19,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#0d211b",
  },
  videoSummary: {
    marginHorizontal: 20,
    marginTop: 16,
    padding: 10,
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#1d4437",
    backgroundColor: "#091b15",
  },
  thumbnail: {
    width: 116,
    height: 66,
    borderRadius: 8,
    backgroundColor: "#020706",
  },
  thumbnailFallback: { alignItems: "center", justifyContent: "center" },
  videoCopy: { flex: 1, minWidth: 0, marginLeft: 12 },
  videoTitle: {
    color: "#edf5f2",
    fontSize: 14,
    lineHeight: 20,
    fontWeight: "700",
  },
  videoUrl: { marginTop: 5, color: "#719388", fontSize: 11 },
  notice: {
    minHeight: 36,
    marginHorizontal: 20,
    marginTop: 10,
    paddingHorizontal: 11,
    flexDirection: "row",
    alignItems: "center",
    gap: 7,
    borderRadius: 8,
    backgroundColor: "rgba(40,201,154,.12)",
    borderWidth: 1,
    borderColor: "rgba(40,201,154,.3)",
  },
  noticeText: { flex: 1, color: "#bdeee0", fontSize: 12 },
  scroll: { minHeight: 0 },
  scrollContent: { paddingHorizontal: 20, paddingTop: 16, paddingBottom: 24 },
  quickActions: { flexDirection: "row", flexWrap: "wrap", gap: 9 },
  primaryAction: {
    minHeight: 42,
    paddingHorizontal: 15,
    borderRadius: 9,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
    backgroundColor: "#28c99a",
  },
  primaryActionText: { color: "#05110d", fontSize: 13, fontWeight: "800" },
  secondaryAction: {
    minHeight: 42,
    paddingHorizontal: 14,
    borderRadius: 9,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
    borderWidth: 1,
    borderColor: "#2f765f",
    backgroundColor: "#0a211a",
  },
  secondaryActionText: { color: "#bdeee0", fontSize: 13, fontWeight: "700" },
  groupTitle: {
    marginTop: 20,
    color: "#f2f5f4",
    fontSize: 15,
    fontWeight: "800",
  },
  groupDescription: {
    marginTop: 4,
    marginBottom: 10,
    color: "#73817d",
    fontSize: 11,
  },
  targetGrid: { marginTop: 10, flexDirection: "row", flexWrap: "wrap", gap: 9 },
  targetButton: {
    width: 150,
    minHeight: 58,
    flexGrow: 1,
    flexBasis: 142,
    maxWidth: 200,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 11,
    borderWidth: 1,
    borderColor: "#214a3d",
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#0a1c16",
    cursor: "pointer",
  } as never,
  targetButtonPressed: { opacity: 0.72, backgroundColor: "#123126" },
  targetIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
  },
  targetCopy: { flex: 1, minWidth: 0, marginLeft: 9 },
  targetLabel: { color: "#edf5f2", fontSize: 12, fontWeight: "800" },
  targetHint: { marginTop: 2, color: "#72827c", fontSize: 10 },
});
